import { useEffect } from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { useClientTheme } from "@/app/components/ThemeContext"
import { useIsMobile } from "@/app/hooks/useIsMobile"

/**
 * Centered modal dialog matching the app's existing Add/Edit Unit sheet: a
 * blurred backdrop, a rounded surface (full-screen on mobile), and a titled
 * header with a close button. Closes on backdrop click or the ✕ — no
 * Escape-to-close, matching the app's other modal and avoiding a clash with the
 * Escape handler on any SelectMenu popover rendered inside it.
 *
 * Portals to <body> so it escapes any card `overflow` / stacking context.
 * `children` are the body — callers own body padding and any footer.
 */
export function Modal({
  title,
  onClose,
  children,
  maxWidth = 560,
}: {
  title: string
  onClose: () => void
  children: React.ReactNode
  maxWidth?: number
}) {
  const { theme } = useClientTheme()
  const isMobile = useIsMobile()

  // Lock background scroll while the modal is open.
  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [])

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex justify-center ${
        isMobile ? "items-stretch" : "items-center p-4"
      }`}
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className={`relative flex flex-col overflow-y-auto ${isMobile ? "" : "rounded-2xl"}`}
        style={{
          backgroundColor: theme.bgSurface,
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
          width: isMobile ? "100vw" : `min(${maxWidth}px, 95vw)`,
          height: isMobile ? "100dvh" : undefined,
          maxHeight: isMobile ? undefined : "90vh",
        }}
      >
        <div
          className="flex items-center justify-between px-6 pt-5 pb-4"
          style={{ borderBottom: `1px solid ${theme.borderColor}` }}
        >
          <span
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "15px",
              fontWeight: 600,
              color: theme.textPrimary,
            }}
          >
            {title}
          </span>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: theme.textMuted,
              padding: "4px",
              display: "flex",
            }}
          >
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  )
}
